import { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import {
  useGetProjectStagesQuery,
  useGetTeamsQuery,
  useGetItemsQuery,
  useGetTagsQuery,
} from 'store/api';
import {
  Calendar,
  CalendarStage,
  CalendarItem,
  AlertDetails,
  AlertDialog,
} from '@centerpiece/components';
import { useNavigate } from 'react-router-dom';
import {
  Item,
  Team,
  ProjectStage,
  PROJECTSTATES,
  ITEMSTATUSES,
  ProjectState,
  Tag,
} from 'store/api/models';
import { getCalendarTypeFromDate } from 'utils/status.utils';
import { useIndexContext } from 'contexts/index.context';
import { ItemTabDrawer } from 'drawers/ItemTabDrawer';

export const CalendarIndexPage = () => {
  const { contactRecord, refetchCount, refetchIndex } = useIndexContext();
  const navigate = useNavigate();
  // BEGIN USESTATE
  const [alertDetails, setAlertDetails] = useState<AlertDetails>();
  const [activeItem, setActiveItem] = useState<any>();
  const [ownershipFilter, setOwnershipFilter] = useState<Team>({
    id: -1,
    name: 'My Projects',
  } as Team);
  const [itemType, setItemType] = useState<{ label: string; filter: string }>({
    label: 'All',
    filter: 'All',
  });
  const [selectedTag, setSelectedTag] = useState<{ name: string; id: number }>({
    name: 'All Tags',
    id: -1,
  });
  // const [runTutorial, setRunTutorial] = useState<Step[]>();
  const [filter, setFilter] = useState<ProjectState>({
    id: 3,
    name: 'Active',
  } as ProjectStage);

  const [month, setMonth] = useState<string>(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  // END USESTATE
  // eslint-disable-next-line

  /////// BEGIN QUERIES
  const { data: stages = [] } = useGetProjectStagesQuery({});
  const { data: teams = [] } = useGetTeamsQuery({});
  const { data: tags = [] } = useGetTagsQuery({});
  const {
    data: itemsPreFilter,
    isFetching: itemsFetching,
    refetch: refetchItems,
  } = useGetItemsQuery(
    {
      params: {
        optimized: 'calendar',
        archived: filter?.id === -1 ? true : undefined,
        rq:
          // ${
          //   ownershipFilter.id === -1
          //     ? `${contactRecord.id}=ANY(contact_id) AND `
          //     : ''
          // }
          // ${
          //   ownershipFilter.id > 0
          //     ? `${ownershipFilter?.id}=ANY(team_ids) AND `
          //     : ''
          // }
          `(( due_date>='${month}' AND ( start_date<='${moment(month)
            .endOf('month')
            .format('YYYY-MM-DD')}' OR due_date<='${moment(month)
            .endOf('month')
            .format(
              'YYYY-MM-DD'
            )}' ) ) OR (start_date>='${month}' AND start_date<='${moment(month)
            .endOf('month')
            .format('YYYY-MM-DD')}'))`,
      },
    },
    { skip: month == '' }
  );
  const items = useMemo(() => {
    return itemsPreFilter?.filter(i => {
      if (ownershipFilter.id === -1) {
        const contact_ids: Number[] = (i as any).contact_id
          ?.slice(1, -1)
          ?.split(',')
          ?.map(Number);
        if (contact_ids?.includes(contactRecord.id)) {
          return true;
        }
      } else if (ownershipFilter.id > 0) {
        const team_ids: Number[] = (i as any).team_ids
          ?.slice(1, -1)
          ?.split(',')
          ?.map(Number);
        if (team_ids?.includes(ownershipFilter?.id)) {
          return true;
        }
      } else {
        return true;
      }
      return false;
    });
  }, [itemsPreFilter, ownershipFilter]);
  useEffect(() => refetchItems(), [refetchCount]);
  /////// END QUERIES

  useEffect(() => {
    if (contactRecord?.form?.authorization === 'Executive') {
      setOwnershipFilter({ id: -2, name: 'All Projects' } as any);
    } else if (
      contactRecord?.form?.authorization === 'Team Leader' &&
      contactRecord?.form?.team
    ) {
      setOwnershipFilter(contactRecord?.form?.team as any);
    }
    // eslint-disable-next-line
  }, [contactRecord]);

  const events: any[] = useMemo(() => {
    let calendarEvents: any[] = [];
    let dates: { [key: string]: any } = {};
    const createEvent = ({
      item,
      icons,
      start,
      end,
      item_type,
      title,
    }: {
      item: Item;
      icons: any[];
      item_type?: string;
      title?: string;
      start?: Date | string;
      end?: Date | string;
    }) => ({
      id: `${item?.id}`,
      editable: false,
      item_type: item_type,
      title: `${item.project_r?.title}: ${title || item?.task_r?.title}`,
      start: start || end,
      end:
        start && end
          ? moment(end).add(1, 'days').format('YYYY-MM-DD')
          : end || start,
      extendedProps: {
        icons: icons,
        node: { ...item, icons: icons },
        todo: {
          icons: icons,
          ...item,
          id: item.id,
          // icon: icon, //add it after
          item_type: item_type,
          project_title: {
            text: item?.project_r?.title,
            project_title: item?.project_r?.title,
            onClick: () => navigate(`/project/${item?.project_r?.id}`),
          },
        },
        tags: item.tags,
        completed:
          item?.status === ITEMSTATUSES.COMPLETE ||
          item.project_r.state === PROJECTSTATES.COMPLETE,
        type:
          item?.status === ITEMSTATUSES.COMPLETE
            ? 'default'
            : getCalendarTypeFromDate(item?.due_date || item?.start_date),
      },
    });
    items?.forEach((item: Item) => {
      if (item.appointment_r && item.appointment_r.start_time) {
        dates[`a-${item.appointment_r.id}` as string] = createEvent({
          item: item,
          icons: ['event'],
          item_type: 'event',
          start: item.appointment_r.start_time,
          end: item.appointment_r.end_time,
        });
      }
      if (item.message_r && item.message_r.due_date) {
        dates[`m-${item.message_r.id}` as string] = createEvent({
          item: item,
          icons:
            item.message_r.type === 'Default' ? ['resp_due'] : ['schedule'],
          item_type:
            item.message_r.type === 'Default' ? 'resp_due' : 'schedule',
          start: item.message_r.due_date,
          end: item.message_r.due_date,
        });
      }
      if (item.task_r && (item.task_r.start_date || item.task_r.due_date)) {
        dates[`t-${item.task_r.id}` as string] = createEvent({
          item: item,
          icons: ['task'],
          item_type: 'task',
          start: item.task_r.start_date,
          end: item.task_r.due_date,
        });
      }
      if (item.stage_r && item.stage_r.due_date) {
        dates[`s-${item.stage_r.id}` as string] = createEvent({
          item: item,
          icons: ['stage'],
          item_type: 'Stage',
          title: item.stage_r.title,
          start: item.stage_r.start_date,
          end: item.stage_r.due_date,
        });
      }
    });
    calendarEvents.push(...Object.values(dates));
    return calendarEvents;
    // eslint-disable-next-line
  }, [items]);

  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <>
      <AlertDialog
        open={!!alertDetails}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        onClose={() => {
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />
      {activeItem?.item_type &&
        ['coordination', 'task', 'event', 'item'].includes(
          activeItem.item_type
        ) && (
          <ItemTabDrawer
            open={true}
            transparentBackdrop={true}
            id={activeItem?.id}
            project_id={activeItem?.project_r?.id}
            afterSave={() => {
              refetchIndex(); //calls refreshRelationships eventually
            }}
            onClose={() => {
              setActiveItem(undefined);
            }}
            breadcrumbs={[
              {
                label: activeItem?.project_title?.text,
                onClick: () =>
                  navigate(`/project/${activeItem?.project_r?.id}`),
              },
              {
                label: activeItem.title,
                onClick: () =>
                  navigate(
                    `/project/${activeItem?.project_r?.id}#i=${activeItem?.item}`
                  ),
              },
            ]}
          />
        )}
      <Calendar
        filters={[
          ...(contactRecord?.form?.authorization === 'Executive' ||
          contactRecord?.form?.authorization === 'Organization Member' ||
          ((contactRecord?.form?.authorization === 'Team Leader' ||
            contactRecord?.form?.authorization === 'Team Member') &&
            contactRecord?.form?.team)
            ? [
                [
                  {
                    options:
                      (contactRecord?.form?.authorization === 'Team Leader' ||
                        contactRecord?.form?.authorization === 'Team Member') &&
                      contactRecord?.form?.team
                        ? [
                            { id: -1, name: 'My Projects' },
                            contactRecord?.form?.team,
                          ]
                        : [
                            { id: -1, name: 'My Projects' },
                            { id: -2, name: 'All Projects' },
                            ...(teams || []),
                          ],
                    label: 'Project Relationship',
                    defaultOption: ownershipFilter,
                    onChange: (event: any, newValue: any) =>
                      setOwnershipFilter(newValue),
                    loading: itemsFetching,
                  },
                ],
              ]
            : []),
          [
            {
              options: [{ id: -1, name: 'All Tags' }, ...(tags || [])],
              label: 'Tag',
              defaultOption: selectedTag,
              onChange: (event: any, newValue: any) => setSelectedTag(newValue),
              loading: itemsFetching,
            },
            {
              options: [
                { label: 'All', filter: 'All' },
                ...stages
                  ?.filter(s => s.subscription)
                  ?.map(s => ({
                    label: `${s.name} stage`,
                    filter: s.name,
                  })),
                { label: 'Item Start/Due Date(s)', filter: 'task' },
                { label: 'Event Date(s)', filter: 'event' },
                { label: 'Message Response Due', filter: 'resp_due' },
                { label: 'Scheduling Due', filter: 'schedule' },
              ],
              label: 'Type',
              defaultOption: itemType,
              onChange: (event, newValue) => setItemType(newValue),
              loading: itemsFetching,
            },
          ],
        ]}
        header={{ expanded, setExpanded }}
        events={
          itemType?.label != 'All' || selectedTag.name !== 'All Tags'
            ? events.filter(e => {
                return (
                  (itemType?.label === 'All' ||
                    e?.extendedProps?.node?.icons?.includes(
                      itemType?.filter.toLowerCase()
                    ) ||
                    (e?.item_type === 'Stage' &&
                      e?.extendedProps?.node?.stage_r?.title.toLowerCase() ===
                        itemType?.filter.toLowerCase())) &&
                  (selectedTag.name === 'All Tags' ||
                    e?.extendedProps?.node?.tags?.filter(
                      (t: Tag) => t.name === selectedTag.name
                    )?.length > 0)
                );
              })
            : events
        }
        eventRender={(event: any) => {
          const props = event.extendedProps;
          const StageRender = CalendarStage;
          const ItemRender = CalendarItem;
          switch (props.item_type.toLowerCase()) {
            case 'stage':
              // const outcomes: Outcome[] =
              //   stages?.find(s => s.id === props.data?.stage?.stage_r?.id)
              //     ?.outcomes || [];
              return (
                <StageRender
                  key={`${props.item_type}-${props?.node?.id}`}
                  documentId={`${props.item_type}-${props?.node?.id}`}
                  {...props?.props}
                  data={{ ...props?.node, title: event?.title }}
                />
              );
            case 'task':
            case 'event':
            case 'coordination':
            case 'item':
            case 'resp_due':
            case 'schedule':
              return (
                <ItemRender
                  key={`${props.item_type}-${props?.node?.id}`}
                  documentId={`${props.item_type}-${props?.node?.id}`}
                  data={props?.node}
                  title={event?.title || ''}
                  icons={props.icons}
                  start_date={event.start}
                  due_date={event.end}
                  complete={
                    props?.node?.status === ITEMSTATUSES.COMPLETE ||
                    props?.node?.complete
                  }
                />
              );
            default:
              return <div />;
          }
        }}
        setMonth={setMonth}
        eventClick={(extendedProps: any) => {
          if (extendedProps.item_type === 'Stage') {
            setAlertDetails({
              title: `Navigate to: ${extendedProps?.todo?.project_title?.text}?`,
              content:
                'Continue if you would like to navigate to the project and view the stage.',
              onConfirm: async () => {
                navigate(`/project/${extendedProps?.node?.project}`);
              },
            });
          } else {
            setActiveItem(extendedProps.todo);
          }
        }}
        month={true}
      />
    </>
  );
};
